import dayjs from 'dayjs'
import React from 'react'

import { apiEndpoints } from 'api'
import {
  statusMap,
  commonAttributes,
  navigationPaths,
  studentSourceTypes,
  tableColumnDefaultOption,
  programList,
  sourceMap
} from 'app/config'
import { sanitize } from 'app/utils'
import { validateName, validateNotEmpty, validateFee } from 'app/validators'

export const columns = [
  {
    ...commonAttributes.uuid,
    options: {
      ...commonAttributes.uuid.options,
      display: false
    }
  },
  {
    name: 'student.user.uuid',
    label: 'Student uuid',
    editable: false,
    options: {
      display: false
    }
  },
  {
    name: 'student.user',
    label: 'Student Name',
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      customBodyRender: (value) => (
        <span>{`${(value && value.first_name) || ''} ${
          (value && value.last_name) || ''
        }`}</span>
      ),
      filterKey: 'student_name'
    }
  },
  {
    ...commonAttributes.standard,
    name: 'student.standard.name',
    label: 'Student Grade',
    editable: false,
    options: {
      filterKey: 'standard'
    }
  },
  {
    name: 'student.parent.user',
    label: 'Parent Name',
    disabledInForm: true,
    validate: validateNotEmpty,
    options: {
      filterKey: 'parent_name',
      customBodyRender: (value) =>
        value ? (
          <span>
            <a
              href={`${navigationPaths.parentdashboard}?parent=${value.uuid}`}>{`${value.first_name} ${value.last_name}`}</a>
          </span>
        ) : null,
      ...tableColumnDefaultOption.options
    }
  },
  {
    name: 'student.parent.user.phone_number',
    label: 'Parent Number',
    disabledInForm: true,
    validate: validateNotEmpty,
    options: {
      filterKey: 'parent_phone_number',
      ...tableColumnDefaultOption.options
    }
  },
  {
    ...commonAttributes.centre,
    name: 'student.parent.centre_preference.name',
    label: 'Centre',
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'centre'
    }
  },
  {
    name: 'status',
    label: 'Status',
    widget: 'select',
    widgetFilter: true,
    valueLabelMappings: (() => {
      const copyStatusMap = { ...statusMap }
      delete copyStatusMap.due
      delete copyStatusMap.lapsed
      copyStatusMap.paused = 'paused'
      return copyStatusMap
    })(),
    disabledInForm: true,
    ...tableColumnDefaultOption
  },
  {
    name: 'program.name',
    label: 'Program Name',
    widget: 'autocomplete',
    widgetFilter: true,
    apiOptions: {
      method: 'get',
      url: `${apiEndpoints.programs}/programs?exclude=school,care,excel,exploration_camp`
    },
    getStructuredValues: (data) => {
      const mapItem = (item) => ({ uuid: item.uuid, name: item.name })

      if (Array.isArray(data)) {
        return data.map(mapItem)
      }

      return (data?.results || []).map(mapItem)
    },
    getFormDataValue: (selected) => {
      if (!selected) return undefined
      return selected.name
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'program_name',
      keyValue: 'name'
    },
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'program_name',
      customBodyRender: (value) => {
        return <div>{value}</div>
      }
    }
  },
  {
    ...commonAttributes.date,
    name: 'start_date',
    label: 'Start Date',
    widget: 'datepicker',
    validate: validateNotEmpty
  },
  {
    ...commonAttributes.date,
    name: 'end_date',
    label: 'End Date',
    disabledInForm: true
  },
  {
    ...commonAttributes.date,
    name: 'adjusted_end_date',
    label: 'Termination Effective Date',
    validate: validateNotEmpty,
    disabledInFormOnEdit: true
  },
  {
    name: 'payment_mode',
    label: 'Payment Mode',
    disabledInFormOnEdit: true,
    widget: 'select',
    validate: validateNotEmpty,
    valueLabelMappings: {
      NO_FEE: 'No Fee',
      PRO_RATED_FEE: 'Pro Rated Fee',
      CUSTOM_FEE: 'Custom Fee'
    },
    options: {
      display: false
    }
  },
  {
    name: 'terminated_by',
    label: 'Terminated By',
    validate: validateNotEmpty,
    disabledInFormOnEdit: true,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => {
        if (!value) return <div>N/A</div>
        return <div>{value?.full_name}</div>
      }
    }
  },
  {
    name: 'termination_reason',
    label: 'Termination Reason',
    validate: validateNotEmpty,
    disabledInFormOnEdit: true,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => {
        return !value ? (
          'N/A'
        ) : (
          <div dangerouslySetInnerHTML={{ __html: sanitize(value) }} />
        )
      },
      setCellProps: () => ({
        style: {
          minWidth: '200px',
          maxWidth: '200px',
          whiteSpace: 'pre-wrap'
        }
      })
    }
  },
  {
    name: 'termination_notes',
    label: 'Termination Notes',
    validate: validateNotEmpty,
    disabledInFormOnEdit: true,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => {
        return !value ? (
          'N/A'
        ) : (
          <div dangerouslySetInnerHTML={{ __html: sanitize(value) }} />
        )
      },
      setCellProps: () => ({
        style: {
          minWidth: '350px',
          maxWidth: '350px',
          whiteSpace: 'pre-wrap'
        }
      })
    }
  },
  {
    name: 'termination_source',
    label: 'Termination Source',
    validate: validateNotEmpty,
    widget: 'select',
    disabledInFormOnEdit: true,
    widgetFilter: true,
    valueLabelMappings: sourceMap,
    isValueKey: true,
    disabledInForm: true,
    ...tableColumnDefaultOption,
    options: {
      customBodyRender: (value) => {
        return !value ? 'N/A' : <div>{sourceMap[value]}</div>
      },
      setCellProps: () => ({
        style: {
          minWidth: '200px',
          maxWidth: '200px',
          whiteSpace: 'pre-wrap'
        }
      })
    }
  }
]
